import dayjs, { Dayjs } from 'utils/dayjs'
import { memo, useCallback, useEffect } from 'react'
import Input from 'core/components/inputs/Input'
import { Controller, useForm } from 'react-hook-form'
import Field from 'core/components/form/Field'

interface DateInputProps {
  date: Dayjs | null | undefined
  onChange: (value: Dayjs) => void
  label: string
  disabled?: boolean
}

const format = 'M/D/YYYY'
const DateInput = ({ date, onChange, label, disabled }: DateInputProps) => {
  const { control, setValue } = useForm<{ date: string }>({
    defaultValues: {
      date: ''
    }
  })

  const isInputDateInvalid = useCallback((_value: string) => {
    const [_month, _day, _year] = _value.split('/')
    if (!!_month?.length && !!_day?.length && _year?.length === 4) {
      const dateValue = dayjs(_value)
      return !dateValue.isValid()
    }

    return true
  }, [])

  const canType = useCallback((_value: string) => {
    const [_month, _day, _year] = _value.split('/')
    if (_day?.length > 2) return false
    if (_month?.length > 2) return false
    if (_year?.length > 4) return false
    if (_value.match(/^[0-9|/]*$/)) return true
    return true
  }, [])

  useEffect(() => {
    if (date && dayjs(date).isValid()) {
      setValue('date', date.format(format))
    }

    if (!date) {
      setValue('date', '')
    }
  }, [date, setValue])

  return (
    <Controller
      control={control}
      name="date"
      render={({ field }) => {
        return (
          <Field label={label} orientation="vertical">
            <Input
              name="date"
              value={
                isInputDateInvalid(field.value)
                  ? field.value
                  : (date?.format(format) ?? field.value)
              }
              placeholder="10/16/2023"
              className="w-full"
              disabled={disabled}
              onChange={(e) => {
                const _value = e.target.value
                if (!canType(_value)) return
                if (!isInputDateInvalid(_value)) {
                  const dateValue = dayjs(_value)
                  onChange(dateValue)
                }
                field.onChange(_value)
              }}
            />
          </Field>
        )
      }}
    />
  )
}

export default memo(DateInput, (prevProps, nextProps) => {
  return (
    prevProps.date?.format('DD/MM/YYYY') ===
      nextProps.date?.format('DD/MM/YYYY') &&
    prevProps.disabled === nextProps.disabled
  )
})
